import FileSaver from 'file-saver';
import { getMetricTotals, MetricRow, MetricTotals } from './metricRows';

type CsvOptions = { hideRecords?: boolean };
type CsvValue = string | number;
type CsvColumn = {
  label: string;
  rowValue: (row: MetricRow) => CsvValue;
  totalValue: (totals: MetricTotals) => CsvValue;
};

const escapeCsvField = (value: string | number): string => {
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const toCsvLine = (fields: CsvValue[]): string => fields.map(escapeCsvField).join(',');

const getMetricsCsvColumns = (hideRecords: boolean): CsvColumn[] => {
  const columns: CsvColumn[] = [
    { label: 'Date', rowValue: (row) => row.date, totalValue: () => 'Total' },
  ];

  if (!hideRecords) {
    columns.push({
      label: 'Records',
      rowValue: (row) => row.records,
      totalValue: (totals) => totals.records,
    });
  }

  columns.push(
    { label: 'Errors', rowValue: (row) => row.errors, totalValue: (totals) => totals.errors },
    {
      label: 'Data Volume Bytes',
      rowValue: (row) => row.size,
      totalValue: (totals) => totals.size,
    },
  );

  return columns;
};

export function buildMetricsCsv(rows: MetricRow[], options?: CsvOptions): string {
  const hideRecords = options?.hideRecords ?? false;
  const totals = getMetricTotals(rows);
  const columns = getMetricsCsvColumns(hideRecords);

  const lines: string[] = [toCsvLine(columns.map((column) => column.label))];

  rows.forEach((row) => {
    lines.push(toCsvLine(columns.map((column) => column.rowValue(row))));
  });

  lines.push(toCsvLine(columns.map((column) => column.totalValue(totals))));

  return lines.join('\n');
}

const toDateStamp = (value: string | Date): string => {
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  return String(value).slice(0, 10);
};

const RESOURCE_LABEL: Record<string, string> = {
  data_sources: 'source',
  data_sinks: 'destination',
  data_sets: 'nexset',
};

export function buildMetricsCsvFilename(args: {
  start: string | Date;
  end: string | Date;
  resourceType?: string;
  resourceId?: string | number;
  category?: string;
}): string {
  const { start, end, resourceType, resourceId, category } = args;
  const range = `${toDateStamp(start)}_to_${toDateStamp(end)}`;
  const label = resourceType ? (RESOURCE_LABEL[resourceType] ?? resourceType) : undefined;
  if (label && resourceId != null) {
    return `nexla-metrics-${label}-${resourceId}-${range}.csv`;
  }
  // Account-scoped exports (e.g. Utilization Read/Write) have no resource id;
  // a category keeps the chart distinguishable (account-read vs account-write).
  const scope = category ? `account-${category}` : 'account';
  return `nexla-metrics-${scope}-${range}.csv`;
}

export function downloadMetricsCsv(args: {
  rows: MetricRow[];
  start: string | Date;
  end: string | Date;
  hideRecords?: boolean;
  resourceType?: string;
  resourceId?: string | number;
  category?: string;
}): void {
  const { rows, start, end, hideRecords, resourceType, resourceId, category } = args;
  const csv = buildMetricsCsv(rows, { hideRecords });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  FileSaver.saveAs(
    blob,
    buildMetricsCsvFilename({ start, end, resourceType, resourceId, category }),
  );
}
